import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import PlaidLink from '../components/common/PlaidLink';
import plaidService from '../services/plaidService';

const PlaidTest = () => {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [rawData, setRawData] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [accountsData, transactionsData, liabilitiesData] = await Promise.all([
        plaidService.getAccounts().catch(err => {
          console.error('Accounts error:', err);
          return [];
        }),
        plaidService.getTransactions().catch(err => {
          console.error('Transactions error:', err);
          return [];
        }),
        plaidService.getLiabilities().catch(err => {
          console.error('Liabilities error:', err);
          return [];
        })
      ]);

      setAccounts(accountsData);
      setTransactions(transactionsData);
      setLiabilities(liabilitiesData);

      console.log('✅ Data loaded successfully:');
      console.log('Accounts:', accountsData);
      console.log('Transactions:', transactionsData);
      console.log('Liabilities:', liabilitiesData);
    } catch (error) {
      setError('Failed to load financial data: ' + error.message);
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkStatus = useCallback(async () => {
    try {
      const status = await plaidService.checkConnectionStatus();
      setConnectionStatus(status);
      
      if (status.connected) {
        await loadAllData();
      }
    } catch (error) {
      setError('Failed to check connection status: ' + error.message);
      console.error('Check status error:', error);
    }
  }, [loadAllData]);

  // Check connection status on mount
  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const loadRawData = async () => {
    try {
      setLoading(true);
      const response = await fetch('http://localhost:5001/api/plaid/debug/all-data?userId=demo_user');
      if (response.ok) {
        const data = await response.json();
        setRawData(data);
        console.log('🐛 Raw debug data:', data);
      } else {
        setError('Failed to fetch raw debug data');
      }
    } catch (error) {
      setError('Error fetching raw data: ' + error.message);
      console.error('Raw data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaidSuccess = (result) => {
    console.log('🎉 Plaid connection successful:', result);
    // Small delay to let server process the data
    setTimeout(() => {
      checkStatus();
    }, 2000);
  };

  const handlePlaidError = (error) => {
    console.error('💥 Plaid connection error:', error);
    setError('Failed to connect bank account: ' + error.message);
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      await plaidService.disconnectAccount();
      setConnectionStatus({ connected: false });
      setAccounts([]);
      setTransactions([]);
      setLiabilities([]);
      setRawData(null);
      setError(null);
      console.log('🗑️ Disconnected successfully');
    } catch (error) {
      setError('Failed to disconnect account: ' + error.message);
      console.error('Disconnect error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      const response = await fetch('http://localhost:5001/api/plaid/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: 'demo_user' }),
      });
      
      if (response.ok) {
        await loadAllData();
        console.log('🔄 Data refreshed successfully');
      } else {
        const errorData = await response.json();
        setError('Failed to refresh data: ' + errorData.error);
      }
    } catch (error) {
      setError('Error refreshing data: ' + error.message);
      console.error('Refresh error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>Plaid Integration Test & Debug</Text>
      
      {/* Debug Mode Toggle */}
      <View style={styles.section}>
        <TouchableOpacity
          style={[styles.debugToggle, debugMode && styles.debugToggleActive]}
          onPress={() => setDebugMode(!debugMode)}
        >
          <Text style={[styles.debugToggleText, debugMode && styles.debugToggleTextActive]}>
            {debugMode ? '🐛 Debug Mode ON' : '🔧 Debug Mode OFF'}
          </Text>
        </TouchableOpacity>
      </View>
      
      {/* Connection Status */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Connection Status</Text>
        {connectionStatus ? (
          <View style={[
            styles.statusCard,
            connectionStatus.connected ? styles.statusConnected : styles.statusDisconnected
          ]}>
            <Text style={[
              styles.statusText,
              connectionStatus.connected ? styles.statusTextConnected : styles.statusTextDisconnected
            ]}>
              {connectionStatus.connected ? '✅ Connected' : '❌ Not Connected'}
            </Text>
            {connectionStatus.connected && connectionStatus.connectedAt && (
              <Text style={styles.statusDetail}>
                Connected: {new Date(connectionStatus.connectedAt).toLocaleString()}
              </Text>
            )}
            {debugMode && connectionStatus.connected && (
              <View style={styles.debugInfo}>
                <Text style={styles.debugText}>
                  Accounts: {connectionStatus.hasAccounts ? '✅' : '❌'} | 
                  Transactions: {connectionStatus.hasTransactions ? '✅' : '❌'} | 
                  Liabilities: {connectionStatus.hasLiabilities ? '✅' : '❌'}
                </Text>
              </View>
            )}
          </View>
        ) : (
          <Text style={styles.loadingText}>Checking status...</Text>
        )}
      </View>

      {/* Connection Controls */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Actions</Text>
        <View style={styles.buttonContainer}>
          {!connectionStatus?.connected ? (
            <PlaidLink
              onSuccess={handlePlaidSuccess}
              onError={handlePlaidError}
              buttonText="Connect Your Bank Account"
              buttonStyle={styles.connectButton}
            />
          ) : (
            <>
              <TouchableOpacity
                style={styles.refreshButton}
                onPress={handleRefresh}
                disabled={loading}
              >
                <Text style={styles.refreshButtonText}>
                  {loading ? 'Refreshing...' : '🔄 Refresh Data'}
                </Text>
              </TouchableOpacity>
              
              {debugMode && (
                <TouchableOpacity
                  style={styles.debugButton}
                  onPress={loadRawData}
                  disabled={loading}
                >
                  <Text style={styles.debugButtonText}>
                    {loading ? 'Loading...' : '🐛 Load Raw Data'}
                  </Text>
                </TouchableOpacity>
              )}
              
              <TouchableOpacity
                style={styles.disconnectButton}
                onPress={handleDisconnect}
                disabled={loading}
              >
                <Text style={styles.disconnectButtonText}>🗑️ Disconnect</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>

      {/* Error Display */}
      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity
            style={styles.errorButton}
            onPress={() => setError(null)}
          >
            <Text style={styles.errorButtonText}>Dismiss</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Data Display */}
      {connectionStatus?.connected && (
        <>
          {/* Accounts */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Accounts ({accounts.length})</Text>
            {accounts.length === 0 ? (
              <Text style={styles.noDataText}>No accounts data available</Text>
            ) : (
              accounts.map((account, index) => (
                <View key={account.id || index} style={styles.dataCard}>
                  <Text style={styles.dataTitle}>{account.name}</Text>
                  <Text style={styles.dataSubtitle}>{account.type} • {account.institution}</Text>
                  <Text style={styles.dataAmount}>${account.balance?.toLocaleString() || 'N/A'}</Text>
                  {debugMode && account.rawPlaidData && (
                    <Text style={styles.debugText}>
                      Raw Type: {account.rawPlaidData.type}/{account.rawPlaidData.subtype}
                    </Text>
                  )}
                </View>
              ))
            )}
          </View>

          {/* Recent Transactions */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Recent Transactions ({transactions.length})</Text>
            {transactions.length === 0 ? (
              <Text style={styles.noDataText}>No transactions data available</Text>
            ) : (
              <>
                {transactions.slice(0, debugMode ? 10 : 5).map((transaction, index) => (
                  <View key={transaction.id || index} style={styles.dataCard}>
                    <Text style={styles.dataTitle}>{transaction.name}</Text>
                    <Text style={styles.dataSubtitle}>
                      {transaction.category} • {transaction.date}
                    </Text>
                    <Text style={[
                      styles.dataAmount,
                      transaction.amount < 0 ? styles.negativeAmount : styles.positiveAmount
                    ]}>
                      {transaction.amount < 0 ? '-' : '+'}${Math.abs(transaction.amount).toLocaleString()}
                    </Text>
                    {debugMode && transaction.rawPlaidData && (
                      <Text style={styles.debugText}>
                        Account: {transaction.account_id} | 
                        Categories: {transaction.rawPlaidData.category?.join(', ') || 'None'}
                      </Text>
                    )}
                  </View>
                ))}
                {transactions.length > (debugMode ? 10 : 5) && (
                  <Text style={styles.moreText}>
                    ... and {transactions.length - (debugMode ? 10 : 5)} more transactions
                  </Text>
                )}
              </>
            )}
          </View>

          {/* Liabilities */}
          {(liabilities.length > 0 || debugMode) && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Liabilities ({liabilities.length})</Text>
              {liabilities.length === 0 ? (
                <Text style={styles.noDataText}>No liabilities data available</Text>
              ) : (
                liabilities.map((liability, index) => (
                  <View key={liability.id || index} style={styles.dataCard}>
                    <Text style={styles.dataTitle}>{liability.name || liability.type}</Text>
                    <Text style={styles.dataSubtitle}>
                      {liability.type} • {liability.institution}
                    </Text>
                    <Text style={styles.dataAmount}>${liability.balance?.toLocaleString() || 'N/A'}</Text>
                    {liability.monthly_payment && (
                      <Text style={styles.statusDetail}>
                        Monthly Payment: ${liability.monthly_payment.toLocaleString()}
                      </Text>
                    )}
                    {liability.interest_rate && (
                      <Text style={styles.statusDetail}>
                        Interest Rate: {liability.interest_rate}%
                      </Text>
                    )}
                  </View>
                ))
              )}
            </View>
          )}

          {/* Raw Data Debug Section */}
          {debugMode && rawData && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>🐛 Raw Debug Data</Text>
              <View style={styles.debugContainer}>
                <Text style={styles.debugTitle}>Connection Info:</Text>
                <Text style={styles.debugText}>
                  {JSON.stringify(rawData.connection, null, 2)}
                </Text>
                
                <Text style={styles.debugTitle}>Raw Accounts Sample:</Text>
                <Text style={styles.debugText}>
                  {JSON.stringify(rawData.rawData?.accounts?.accounts?.[0], null, 2)}
                </Text>
                
                <Text style={styles.debugTitle}>Raw Transaction Sample:</Text>
                <Text style={styles.debugText}>
                  {JSON.stringify(rawData.rawData?.transactions?.transactions?.[0], null, 2)}
                </Text>
              </View>
            </View>
          )}
        </>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  content: {
    maxWidth: 768,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 24,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 12,
  },
  debugToggle: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  debugToggleActive: {
    backgroundColor: '#EFF6FF',
    borderWidth: 1,
    borderColor: '#2563EB',
  },
  debugToggleText: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
  },
  debugToggleTextActive: {
    color: '#2563EB',
  },
  statusCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderWidth: 2,
  },
  statusConnected: {
    borderColor: '#10B981',
    backgroundColor: '#ECFDF5',
  },
  statusDisconnected: {
    borderColor: '#EF4444',
    backgroundColor: '#FEF2F2',
  },
  statusText: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  statusTextConnected: {
    color: '#059669',
  },
  statusTextDisconnected: {
    color: '#DC2626',
  },
  statusDetail: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
  },
  debugInfo: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  debugText: {
    fontSize: 10,
    color: '#6B7280',
    fontFamily: 'monospace',
  },
  loadingText: {
    fontSize: 14,
    color: '#6B7280',
    fontStyle: 'italic',
  },
  noDataText: {
    fontSize: 14,
    color: '#9CA3AF',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 16,
  },
  buttonContainer: {
    gap: 12,
  },
  connectButton: {
    backgroundColor: '#2563EB',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  refreshButton: {
    backgroundColor: '#059669',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  refreshButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  debugButton: {
    backgroundColor: '#7C3AED',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  debugButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  disconnectButton: {
    backgroundColor: '#DC2626',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  disconnectButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  errorContainer: {
    backgroundColor: '#FEF2F2',
    borderColor: '#FECACA',
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 16,
  },
  errorText: {
    color: '#DC2626',
    fontSize: 14,
    marginBottom: 8,
  },
  errorButton: {
    backgroundColor: '#DC2626',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  errorButtonText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  dataCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  dataTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  dataSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 4,
  },
  dataAmount: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#111827',
  },
  positiveAmount: {
    color: '#059669',
  },
  negativeAmount: {
    color: '#DC2626',
  },
  moreText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    fontStyle: 'italic',
    marginTop: 8,
  },
  debugContainer: {
    backgroundColor: '#1F2937',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    maxHeight: 300,
  },
  debugTitle: {
    fontSize: 12,
    color: '#F9FAFB',
    fontWeight: '600',
    marginTop: 8,
    marginBottom: 4,
  },
});

export default PlaidTest; 